import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, RefreshCw, AlertCircle, ShieldOff, ShieldCheck } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

interface WorkerRecord {
  name: string;
  display_name: string;
  enabled: boolean;
  last_heartbeat: string | null;
  updated_at: string;
  updated_by?: string | null;
}

// Brief descriptions for the seeded worker set. Backend owns display_name,
// the long description stays on the UI side because it's user-facing copy
// (not state). New workers without an entry fall back to a generic line.
const workerDescriptions: Record<string, string> = {
  'aeo-realtime-generator': 'Generates AEO answers on-demand as new product pages are added. Off → new pages stay in the queue until the batch drain catches up.',
  'aeo-batch-drain': 'Background drainer that processes any AEO work the realtime path didn\'t pick up. Off → queue grows; turn back on to catch up.',
  'run-realtime-translations': 'Translates fresh content into target languages as soon as it lands. Off → translations queue up for the next batch run.',
};

function formatHeartbeat(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Unknown';
  const ageSec = Math.floor((Date.now() - then) / 1000);
  if (ageSec < 60) return `${ageSec}s ago`;
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

// Stale-cutoff: heartbeats older than this paint the badge amber even
// when the worker is "enabled". Matches a worker missing 3+ poll cycles
// at a typical 60s heartbeat — adjust if backend cadence changes.
const STALE_HEARTBEAT_MS = 3 * 60 * 1000;

function isHeartbeatStale(iso: string | null): boolean {
  if (!iso) return true;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return true;
  return Date.now() - then > STALE_HEARTBEAT_MS;
}

export default function WorkerControls() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [workers, setWorkers] = useState<WorkerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-worker pending state so toggle clicks block re-fire while
  // PUT is in flight (otherwise a fast double-click can land
  // enabled=false then enabled=true out of order).
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const fetchWorkers = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/admin/workers');
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`GET /admin/workers ${response.status} — ${body || response.statusText}`);
      }
      const data: WorkerRecord[] = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('GET /admin/workers returned non-array body');
      }
      // Sort by display_name for stable card order.
      data.sort((a, b) => a.display_name.localeCompare(b.display_name));
      setWorkers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchWorkers(false);
  }, [isSuperAdmin, fetchWorkers]);

  const handleToggle = async (worker: WorkerRecord, nextEnabled: boolean) => {
    if (pending[worker.name]) return;
    setPending((p) => ({ ...p, [worker.name]: true }));
    // Optimistic update — flip locally so the switch responds
    // immediately; PUT failure rolls back below.
    setWorkers((ws) => ws.map((w) => (w.name === worker.name ? { ...w, enabled: nextEnabled } : w)));

    try {
      const response = await authFetch(`/admin/workers/${encodeURIComponent(worker.name)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`PUT /admin/workers/${worker.name} ${response.status} — ${body || response.statusText}`);
      }
      const updated: WorkerRecord = await response.json();
      setWorkers((ws) => ws.map((w) => (w.name === worker.name ? updated : w)));
    } catch (err) {
      // Roll back the optimistic flip + surface the error inline.
      setWorkers((ws) => ws.map((w) => (w.name === worker.name ? { ...w, enabled: worker.enabled } : w)));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending((p) => {
        const { [worker.name]: _drop, ...rest } = p;
        return rest;
      });
    }
  };

  if (!isSuperAdmin) {
    return (
      <Alert variant="destructive">
        <ShieldOff className="h-4 w-4" />
        <AlertTitle>Access denied</AlertTitle>
        <AlertDescription>
          Worker controls are restricted to super administrators.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Worker Controls</h1>
          <p className="text-muted-foreground mt-1">
            Toggle realtime generation workers on or off. Changes take effect on the next poll cycle (typically &lt;1 min).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchWorkers(true)}
          disabled={refreshing || loading}
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Worker control error</AlertTitle>
          <AlertDescription className="break-all">{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading workers…
        </div>
      )}

      {!loading && workers.length === 0 && !error && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No workers registered yet. The backend will surface them once they report a heartbeat.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {workers.map((worker) => {
          const stale = isHeartbeatStale(worker.last_heartbeat);
          const description = workerDescriptions[worker.name]
            ?? 'Background worker. Toggle off to pause; toggle on to resume.';
          return (
            <Card
              key={worker.name}
              className={worker.enabled ? '' : 'opacity-70 border-destructive/40'}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {worker.display_name}
                      {worker.enabled ? (
                        <Badge variant="default" className="bg-green-600">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <ShieldOff className="h-3 w-3 mr-1" />
                          Disabled
                        </Badge>
                      )}
                      {worker.enabled && stale && (
                        <Badge variant="outline" className="border-amber-500 text-amber-700">
                          Stale heartbeat
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="font-mono text-xs mt-1">
                      {worker.name}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {pending[worker.name] && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <Switch
                      checked={worker.enabled}
                      disabled={!!pending[worker.name]}
                      onCheckedChange={(checked) => handleToggle(worker, checked)}
                      aria-label={`Toggle ${worker.display_name}`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">{description}</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">Last heartbeat</dt>
                  <dd>{formatHeartbeat(worker.last_heartbeat)}</dd>
                  <dt className="text-muted-foreground">Last config update</dt>
                  <dd>{formatHeartbeat(worker.updated_at)}</dd>
                  {worker.updated_by && (
                    <>
                      <dt className="text-muted-foreground">Changed by</dt>
                      <dd className="break-all">{worker.updated_by}</dd>
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
